/**
 * WCAG 3.1.2 (Language of Parts) — resolves the language of course content
 * so it can be exposed via a `lang` attribute on the element that renders it.
 *
 * Course content (Mint viewer, structure names) may be authored in a language
 * different from the UI. Marking it with a valid BCP-47 `lang` lets screen
 * readers switch pronunciation accordingly. Granularity is course-level only
 * (the backend provides no per-lesson language).
 *
 * Returning `undefined` means "omit the attribute": React does not render
 * `lang={undefined}`, so the element inherits the surrounding page language.
 */

type CourseWithLang = {
  digital_program_langs?: { id?: string }[];
  program_lang?: string;
} | null | undefined;

// Whitelist propia de tags BCP-47 aceptados como idioma de contenido. Es independiente
// de SUPPORTED_LOCALES (numberFormat.ts): aquella es para Intl.NumberFormat y solo admite
// locales region-qualified (TAN-8110), mientras que el backend manda códigos base
// (`es`, `en`, `pt`…) en digital_program_langs. Un idioma válido fuera de esta lista
// (p. ej. `fr`) no se marca — a propósito, se omite el atributo.
const CONTENT_LANG_WHITELIST = [
  'es',
  'en',
  'pt',
  'ca',
  'eu',
  'gl',
  'es-AR',
  'es-CO',
  'es-MX',
  'es-ES',
  'es-CR',
  'es-PA',
  'es-NI',
  'en-US',
  'en-GB',
  'pt-BR',
] as const;

// Maps app-internal content codes that are not valid BCP-47 tags to valid ones.
const CONTENT_LANG_MAP: Record<string, string> = {
  br: 'pt-BR',
  mx: 'es-MX',
  vl: 'ca',
};

/**
 * Normalizes a raw content-language code into a valid BCP-47 tag.
 *
 * Shared by `getContentLang` (course-level) and any surface that owns its own
 * language field (e.g. a book's `language`), so the whitelist + app-code mapping
 * (`br → pt-BR`, `mx → es-MX`, `vl → ca`) lives in a single place.
 *
 * @param raw - Raw language code as authored in the backend.
 * @returns A valid BCP-47 tag, or `undefined` when the code is unknown or missing
 *          (so the `lang` attribute is omitted and inheritance applies).
 */
export function normalizeContentLang(raw: string | undefined | null): string | undefined {
  if (!raw) return undefined;

  if ((CONTENT_LANG_WHITELIST as readonly string[]).includes(raw)) return raw;

  return CONTENT_LANG_MAP[raw];
}

/**
 * Resolves the content language of a course as a valid BCP-47 tag.
 *
 * @param course - Course whose content language is being resolved.
 * @returns A valid BCP-47 tag, or `undefined` when the language is unknown or
 *          missing (so the `lang` attribute is omitted and inheritance applies).
 */
export function getContentLang(course: CourseWithLang): string | undefined {
  // `||` (no `??`): si el backend enviara `digital_program_langs: [{ id: '' }]`,
  // el string vacío debe caer igualmente a `program_lang`.
  const raw = course?.digital_program_langs?.[0]?.id || course?.program_lang;

  return normalizeContentLang(raw);
}
